var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as ndarray from 'ndarray';
import * as ops from 'ndarray-ops';
import { convertPdfToImages, FileInputCaptureEnum, TileMode } from '@mitchell/react.component.ux';
import { convertBlobToBase64, convertBlobToFile, isJpegImage, PDF_THUMBNAIL, processJpegImageExif } from "../../common";
import { FileHelperService, ImageScalingService } from "../../file";
import { PhotoCapture, PhotoCaptureAttributeType } from '../enums';
import { GUIDANCE_MESSAGES, IntelligentAlbumHierarchy, IntelligentImageAnalyzerCutOff, IntelligentImageQualityScoreCutOffs, IntelligentPhotoCapture, IntelligentPhotoCapturePhotoPreviewConstants, IntelligentPhotoCaptureScreenConstant, KERNEL, PhotoCaptureAllowedMimeTypeConstant, PhotoCaptureConstants, ROUND_PRECISION, XYZ_DIVISOR_X, XYZ_DIVISOR_Y, XYZ_DIVISOR_Z } from '../constants';
import { photoCaptureAllowedMimeType } from './photoCaptureCommonUtilities';
const { BLUR_MIN, BLUR_MAX, GAMMA_MIN, GAMMA_MAX, BRIGHTNESS_MIN, BRIGHTNESS_MAX, CONTRAST_MIN, CONTRAST_MAX, MID_GAMMA } = IntelligentImageQualityScoreCutOffs;
export const getScreenTitleText = (questionSubType, sequenceScreeName) => {
    switch (questionSubType) {
        case PhotoCaptureAttributeType.LICENSE_PLATE_CAPTURE:
        case PhotoCaptureAttributeType.SINGLE_WIDE_SHOT:
        case PhotoCaptureAttributeType.MILEAGE_CAPTURE:
        case PhotoCaptureAttributeType.VIN_CAPTURE:
            return IntelligentPhotoCaptureScreenConstant[questionSubType];
        case PhotoCaptureAttributeType.THREE_CLOSE_UP:
        case PhotoCaptureAttributeType.FOUR_CORNERS_CAPTURE:
            return IntelligentPhotoCaptureScreenConstant[questionSubType][sequenceScreeName];
        case PhotoCaptureAttributeType.WIDE_ENHANCED:
        case PhotoCaptureAttributeType.CLOSEUP_ENHANCED:
            return IntelligentPhotoCaptureScreenConstant['PRG_ENHANCED'][sequenceScreeName];
        default: {
            return IntelligentPhotoCaptureScreenConstant[questionSubType];
        }
    }
};
export const getNavigationMultipleButtonProps = ({ appTheme, onLeftClick, onRightClick }) => {
    return {
        buttonBackgroundColor: appTheme.buttonBackgroundColor,
        buttonTextColor: appTheme.buttonTextColor,
        secondaryButtonBackgroundColor: appTheme.secondaryButtonBackgroundColor,
        secondaryButtonTextColor: appTheme.secondaryButtonTextColor,
        idPrefix: `${IntelligentPhotoCapture.INTELLIGENT_GUIDED_PHOTO_CAPTURE}_NavigationControlComponent`,
        shouldNextButtonDisabled: false,
        shouldPrevButtonDisabled: false,
        isLeftBtnSelected: true,
        selectedButtonBackgroundColor: appTheme.selectedButtonBackgroundColor,
        disabledButtonBackgroundColor: appTheme.disabledButtonBackgroundColor,
        disabledButtonTextColor: appTheme.disabledButtonTextColor,
        leftButtonIcon: PhotoCaptureConstants.ICON_CLASS,
        leftButtonLabel: IntelligentPhotoCapturePhotoPreviewConstants.RETAKE_PHOTO_BUTTON_LABEL,
        onLeftClick: onLeftClick,
        onRightClick: onRightClick
    };
};
export const getGuidanceMessage = (scores) => {
    if (scores.brightness < BRIGHTNESS_MIN) {
        return GUIDANCE_MESSAGES.ADJUST_LIGHTING;
    }
    else if (scores.brightness > BRIGHTNESS_MAX) {
        return GUIDANCE_MESSAGES.REDUCE_LIGHTING;
    }
    else if (scores.blur > BLUR_MAX || scores.blur < BLUR_MIN) {
        return GUIDANCE_MESSAGES.BLURRY_IMAGE;
    }
    else if (scores.gamma < GAMMA_MIN) {
        return GUIDANCE_MESSAGES.LOW_GAMMA;
    }
    else if (scores.gamma > GAMMA_MAX) {
        return GUIDANCE_MESSAGES.HIGH_GAMMA;
    }
    else if (scores.contrast < CONTRAST_MIN) {
        return GUIDANCE_MESSAGES.LOW_CONTRAST;
    }
    else if (scores.contrast > CONTRAST_MAX) {
        return GUIDANCE_MESSAGES.HIGH_CONTRAST;
    }
    else {
        return GUIDANCE_MESSAGES.DEFAULT_MESSAGE;
    }
};
export const rgbToXyz = (r, g, b) => {
    let red = r;
    let green = g;
    let blue = b;
    red = red > 0.04045 ? Math.pow((red + 0.055) / 1.055, 2.4) : red / 12.92;
    green = green > 0.04045 ? Math.pow((green + 0.055) / 1.055, 2.4) : green / 12.92;
    blue = blue > 0.04045 ? Math.pow((blue + 0.055) / 1.055, 2.4) : blue / 12.92;
    red *= 100;
    green *= 100;
    blue *= 100;
    const x = red * 0.4124 + green * 0.3576 + blue * 0.1805;
    const y = red * 0.2126 + green * 0.7152 + blue * 0.0722;
    const z = red * 0.0193 + green * 0.1192 + blue * 0.9505;
    return { l: x, a: y, b: z };
};
export const xyzToLab = (x, y, z) => {
    let X = x / XYZ_DIVISOR_X;
    let Y = y / XYZ_DIVISOR_Y;
    let Z = z / XYZ_DIVISOR_Z;
    X = X > 0.008856 ? Math.pow(X, 1 / 3) : 7.787 * X + 16 / 116;
    Y = Y > 0.008856 ? Math.pow(Y, 1 / 3) : 7.787 * Y + 16 / 116;
    Z = Z > 0.008856 ? Math.pow(Z, 1 / 3) : 7.787 * Z + 16 / 116;
    const L = 116 * Y - 16;
    const A = 500 * (X - Y);
    const B = 200 * (Y - Z);
    return { l: L, a: A, b: B };
};
export const convertToLAB = (imageData) => {
    const { data, width, height } = imageData;
    const labArray = new Array(height);
    const grayArray = [];
    for (let y = 0; y < height; y++) {
        labArray[y] = new Array(width);
        for (let x = 0; x < width; x++) {
            const index = (y * width + x) * 4;
            const r = data[index] / 255;
            const g = data[index + 1] / 255;
            const b = data[index + 2] / 255;
            grayArray.push(Math.round(0.299 * data[index] + 0.587 * data[index + 1] + 0.114 * data[index + 2]));
            const xyz = rgbToXyz(r, g, b);
            const lab = xyzToLab(xyz.l, xyz.a, xyz.b);
            labArray[y][x] = lab;
        }
    }
    return { lab: labArray, grayArray };
};
export const calculateGamma = (grayArray) => {
    let gamma;
    const ndgrayArray = ndarray(new Float32Array(grayArray), [grayArray.length]);
    const sum = ops.sum(ndgrayArray);
    const mean = sum / ndgrayArray.shape[0];
    if (mean !== 0) {
        gamma = Math.log(MID_GAMMA * 255) / Math.log(mean);
        gamma = Math.round(gamma * ROUND_PRECISION) / ROUND_PRECISION;
    }
    else {
        gamma = 0;
    }
    return gamma;
};
export const calculateBrightnessContrast = (lab) => {
    const lData = lab.flat().map(val => val.l);
    const ndlData = ndarray(new Float32Array(lData), [lData.length]);
    const min = ops.inf(ndlData);
    const max = ops.sup(ndlData);
    const lb = min;
    const ub = max;
    if (min + max === 0) {
        throw new Error('ub + lb equals to 0');
    }
    const contrast = Math.round(((ub - lb) / (ub + lb)) * ROUND_PRECISION) / ROUND_PRECISION;
    if (ub === 0) {
        throw new Error('Max value is 0');
    }
    const normalizedData = ops.divs(ndlData, ndlData, ub);
    const ndgrayArray = ndarray(new Float32Array(normalizedData.data), [normalizedData.shape[0]]);
    const sum = ops.sum(ndgrayArray);
    const mean = sum / ndgrayArray.shape[0];
    const brightnessString = mean.toFixed(2);
    const brightness = parseFloat(brightnessString);
    return { contrast, brightness };
};
export const calculateBlur = (imageData) => {
    const { data, width, height } = imageData;
    let laplacian = [];
    for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
            let sum = 0;
            for (let ky = -1; ky <= 1; ky++) {
                for (let kx = -1; kx <= 1; kx++) {
                    const ni = ((y + ky) * width + (x + kx)) * 4;
                    const r = data[ni];
                    const g = data[ni + 1];
                    const b = data[ni + 2];
                    const grayNeighbor = 0.299 * r + 0.587 * g + 0.114 * b;
                    sum += grayNeighbor * KERNEL[(ky + 1) * 3 + (kx + 1)];
                }
            }
            laplacian.push(sum);
        }
    }
    const ndlaplacian = ndarray(new Float32Array(laplacian), [laplacian.length]);
    const sum = ops.sum(ndlaplacian);
    const mean = sum / ndlaplacian.shape[0];
    const variance = laplacian.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / laplacian.length;
    return variance.toFixed(2);
};
export const getImageQualityScore = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => {
            const canvasCtx = getCanvas(img);
            let imageData = new ImageData(1, 1);
            if (canvasCtx) {
                canvasCtx.drawImage(img, 0, 0, img.width, img.height);
                imageData = canvasCtx.getImageData(0, 0, img.width, img.height);
            }
            const { isClear, scores } = getImageAnalysis(imageData);
            resolve({ isClear, scores });
        };
        img.onerror = error => {
            reject(`Failed to load image: ${error}`);
        };
    });
};
const getCanvas = (img) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = img.width;
    canvas.height = img.height;
    return ctx;
};
export const hasImageIsClear = (scores) => {
    return (scores.brightness >= BRIGHTNESS_MIN &&
        scores.brightness <= BRIGHTNESS_MAX &&
        scores.contrast >= CONTRAST_MIN &&
        scores.contrast <= CONTRAST_MAX &&
        scores.blur >= BLUR_MIN &&
        scores.blur <= BLUR_MAX &&
        scores.gamma >= GAMMA_MIN &&
        scores.gamma <= GAMMA_MAX);
};
const getImageAnalysis = (imageData) => {
    const { lab, grayArray } = convertToLAB(imageData);
    const gamma = calculateGamma(grayArray);
    const { contrast, brightness } = calculateBrightnessContrast(lab);
    const blur = calculateBlur(imageData);
    const scores = { brightness, contrast, blur: parseFloat(blur), gamma };
    const isClear = hasImageIsClear(scores);
    return { isClear, scores };
};
export const getAspectRatio = (val, lim) => {
    var lower = [0, 1];
    var upper = [1, 0];
    while (true) {
        var mediant = [lower[0] + upper[0], lower[1] + upper[1]];
        if (val * mediant[1] > mediant[0]) {
            if (lim < mediant[1]) {
                return upper;
            }
            lower = mediant;
        }
        else if (val * mediant[1] == mediant[0]) {
            if (lim >= mediant[1]) {
                return mediant;
            }
            if (lower[1] < upper[1]) {
                return lower;
            }
            return upper;
        }
        else {
            if (lim < mediant[1]) {
                return lower;
            }
            upper = mediant;
        }
    }
};
export const createDefaultImageDetailsLoaders = () => {
    return {
        isBrightnessScoreLoader: null,
        isContrastScoreLoader: null,
        isGammaScoreLoader: null,
        isBlurScoreLoader: null,
        isNoiseScoreLoader: null
    };
};
export const getCompressImage = (file, scaledMaxSize) => __awaiter(void 0, void 0, void 0, function* () {
    let fileBase64;
    if (isJpegImage(file.name)) {
        const originFile = file;
        const resizedFile = (yield ImageScalingService.compressImage(file, scaledMaxSize));
        try {
            file = yield processJpegImageExif(originFile, resizedFile.name, resizedFile);
        }
        catch (e) {
            // when error occurs during processing JPEG image EXIF, only move forward to upload resized file without EXIF
            file = resizedFile;
        }
    }
    else if (file.name.lastIndexOf(PDF_THUMBNAIL) !== -1) {
        const image = yield convertPdfToImages(file);
        const imageFile = FileHelperService.dataURLtoFile(image, `${file.name.split('.')[0]}.png`);
        file = convertBlobToFile(file.name, yield ImageScalingService.compressImage(imageFile, scaledMaxSize));
    }
    else {
        file = (yield ImageScalingService.compressImage(file, scaledMaxSize));
    }
    fileBase64 = yield convertBlobToBase64(file);
    return new Promise(function (resolve) {
        resolve({
            file,
            fileBase64
        });
    });
});
export const hasImageQualityAnalysisIsClear = (scores) => {
    return (scores.brightnessScore >= IntelligentImageAnalyzerCutOff.BRIGHTNESS_LOW_CUTOFF &&
        scores.brightnessScore <= IntelligentImageAnalyzerCutOff.BRIGHTNESS_HIGH_CUTOFF &&
        scores.contrastScore >= IntelligentImageAnalyzerCutOff.CONTRAST_LOW_CUTOFF &&
        scores.contrastScore <= IntelligentImageAnalyzerCutOff.CONTRAST_HIGH_CUTOFF &&
        scores.blurScore >= IntelligentImageAnalyzerCutOff.FOCUS_LOW_CUTOFF &&
        scores.blurScore <= IntelligentImageAnalyzerCutOff.FOCUS_HIGH_CUTOFF &&
        scores.gammaScore >= IntelligentImageAnalyzerCutOff.COLOR_LOW_CUTOFF &&
        scores.gammaScore <= IntelligentImageAnalyzerCutOff.COLOR_HIGH_CUTOFF &&
        scores.noiseScore >= IntelligentImageAnalyzerCutOff.LIGHTING_LOW_CUTOFF &&
        scores.noiseScore <= IntelligentImageAnalyzerCutOff.LIGHTING_HIGH_CUTOFF);
};
/**
 * Creates and returns the default gallery props state
 * @returns The initial gallery props state
 */
export const getDefaultGalleryProps = () => ({
    id: IntelligentAlbumHierarchy.ID_ALBUM_HIERARCHY_GALLERY_WRAPPER,
    mode: TileMode.SMALL,
    albums: [],
    tilesGap: IntelligentAlbumHierarchy.TILES_GAP,
    smallTileWidth: IntelligentAlbumHierarchy.SMALL_TILE_WIDTH,
    smallTileHeight: IntelligentAlbumHierarchy.SMALL_TILE_HEIGHT,
    shouldShowThumbnailForPdf: false
});
/**
 * Creates file input props based on the given parameters
 *
 * @param companyCode - Company code for the application
 * @param fileSizeLimit - Maximum file size limit in bytes
 * @param elementWrapper - Function that returns a div element wrapper
 * @param disableLibrary - Whether to disable library access
 * @param imageMaxWidth - Maximum width for scaled images
 * @param maxFileCount - Maximum number of files allowed
 * @returns File input props configuration
 */
export const createFileInputProps = (companyCode, fileSizeLimit, elementWrapper, disableLibrary, imageMaxWidth, maxFileCount) => {
    let fileUploaderProps = {
        applicationName: PhotoCaptureConstants.PHOTO_CAPTURE_APPLICATION_NAME,
        companyCode: companyCode,
        acceptedMimeTypes: photoCaptureAllowedMimeType,
        disabled: false,
        element: elementWrapper(),
        isDuplicateDocumentAllowed: false,
        scaledMaxSize: imageMaxWidth,
        thumbnailMaxSize: PhotoCaptureConstants.THUMBNAIL_MAX_SIZE,
        multiple: true,
        id: `${PhotoCapture.GUIDED_PHOTO_CAPTURE}_FileInput`,
        allowedFileTypes: PhotoCaptureAllowedMimeTypeConstant,
        maxFileSize: fileSizeLimit
    };
    if (maxFileCount) {
        fileUploaderProps.maxFileCount = maxFileCount;
    }
    if (disableLibrary === PhotoCaptureConstants.Y) {
        fileUploaderProps = Object.assign(Object.assign({}, fileUploaderProps), { acceptedMimeTypes: PhotoCaptureConstants.ACCEPT_IMAGE_MIME, captureMode: FileInputCaptureEnum.ENVIRONMENT });
    }
    return fileUploaderProps;
};
//# sourceMappingURL=helper.js.map